import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, type DefaultValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useUI } from '@/hooks/ui/useUI';
import { useUnsavedChangesWarning } from '@/hooks/ui/navigation';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { useTranslation } from '@/i18n';
import { ROUTE_DEFINITIONS } from '@/routes';
import { workingDayService } from '@/api/domains/workingDay';
import type { WorkingDayItem } from '@/api/domains/workingDay';
import { workingDayTypeCatalogService, WORKING_DAY_TYPE_ID } from '@/api/domains/catalog/workingDayType';
import { progressivityService } from '@/api/domains/attendance';
import type { SelectorOption } from '@/components/ui/inputs/selector/Selector.types';
import {
  createShiftDescriptionSchema,
  STATIC_WORKING_DAY_TYPES,
  type CreateShiftDescriptionForm,
  type CreateShiftDescriptionFormSubset,
} from '@/schemas/shift/createShiftDescriptionForm';


const WORKING_DAY_CATALOG_PAGE_SIZE = 1000;
const PARTIAL_WORKING_DAY_ORDINARY_HOURS = 30;
const DAYS_PER_WEEK = 7;
// ASIS-5053 (QA): Bisemanal también queda fijo — en 2 semanas, no en 1 — para que la
// grilla siempre se construya sobre exactamente 14 días y el objetivo dinámico de horas
// (días del patrón ÷ 7 × progresividad) coincida con lo que el usuario puede asignar.
const LOCKED_WEEKS_COUNT_BY_TYPE: Record<number, number> = {
  [WORKING_DAY_TYPE_ID.FIXED]: 1,
  [WORKING_DAY_TYPE_ID.PARTIAL]: 1,
  [WORKING_DAY_TYPE_ID.BIWEEKLY]: 2,
};

const DEFAULT_VALUES: DefaultValues<CreateShiftDescriptionForm> = {
  workingDayTypeId: null,
  workingDayId: null,
  shiftCode: '',
  shiftName: '',
  weeksCount: 1,
  isExceptionalWorkingDay: false,
  dtAuthorizationNumber: '',
  dtAuthorizationDate: '',
  staticStartDate: '',
};

const RESET_ON_TYPE_CHANGE_FIELDS = [
  'workingDayId',
  'shiftCode',
  'shiftName',
  'weeksCount',
  'staticStartDate',
  'isExceptionalWorkingDay',
  'dtAuthorizationNumber',
  'dtAuthorizationDate',
] as const;

export interface UseShiftCreateResult {
  formSubset: CreateShiftDescriptionFormSubset;
  isLoadingCatalogs: boolean;
  isDirty: boolean;
  workingDayTypeOptions: SelectorOption[];
  workingDayOptions: SelectorOption[];
  selectedWorkingDayTypeId: number | null;
  selectedWorkingDay: WorkingDayItem | undefined;
  isDistributedCyclesType: boolean;
  isExceptionalType: boolean;
  isStaticType: boolean;
  ordinaryHours: number | null;
  weeksCountEditable: boolean;
  isGridVisible: boolean;
  handleCancel: () => void;
  confirmNavigation: () => void;
}

export function useShiftCreate(): UseShiftCreateResult {
  const { t: translate } = useTranslation('schedule');
  const { showNotification } = useUI();
  const navigate = useNavigate();

  const {
    register,
    watch,
    setValue,
    trigger,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm<CreateShiftDescriptionForm>({
    resolver: zodResolver(createShiftDescriptionSchema),
    mode: 'onBlur',
    defaultValues: DEFAULT_VALUES,
  });

  const formSubset: CreateShiftDescriptionFormSubset = {
    register, setValue, watch, errors, trigger,
  };

  // ── Catálogos: mismos endpoints que la pestaña "Turnos" (ASIS-5049) ───────
  const {
    options: workingDayTypeOptions,
    isLoaded: isTypesLoaded,
  } = useCatalogLoader(
    (signal) => workingDayTypeCatalogService.getWorkingDayTypes(signal),
    (item) => ({ value: String(item.id), label: item.description }),
    [],
    false,
    () => showNotification('error', translate('shift.create.messages.loadCatalogsError')),
  );

  const { data: workingDays, isLoaded: isWorkingDaysLoaded } = useCatalogLoader(
    async (signal) => {
      const response = await workingDayService.list(
        { pageSize: WORKING_DAY_CATALOG_PAGE_SIZE },
        signal,
      );
      if (!response.success || !response.data) return { ...response, data: null };
      return { ...response, data: response.data.items };
    },
    (item: WorkingDayItem) => ({ value: String(item.id), label: item.name }),
    [],
    false,
    () => showNotification('error', translate('shift.create.messages.loadCatalogsError')),
  );

  const { data: progressivities, isLoaded: isProgressivitiesLoaded } = useCatalogLoader(
    () => progressivityService.getProgressivities(),
    (item) => ({ value: item.startDate, label: String(item.weeklyHours) }),
    [],
    false,
    () => showNotification('error', translate('shift.create.messages.loadCatalogsError')),
  );

  const isLoadingCatalogs = !isTypesLoaded || !isWorkingDaysLoaded || !isProgressivitiesLoaded;

  const selectedWorkingDayTypeId = watch('workingDayTypeId');
  const selectedWorkingDayId = watch('workingDayId');

  // La grilla de distribución se revela apenas hay tipo + jornada seleccionados — ya no
  // depende de un botón "Generar" (ASIS-5054, feedback PO: saltar ese paso intermedio).
  const isGridVisible = selectedWorkingDayTypeId !== null && selectedWorkingDayId !== null;

  const isDistributedCyclesType =
    selectedWorkingDayTypeId === WORKING_DAY_TYPE_ID.DISTRIBUTED_CYCLES;
  const isExceptionalType = selectedWorkingDayTypeId === WORKING_DAY_TYPE_ID.EXCEPTIONAL;
  const isStaticType =
    selectedWorkingDayTypeId !== null &&
    STATIC_WORKING_DAY_TYPES.includes(selectedWorkingDayTypeId);
  const weeksCountEditable =
    selectedWorkingDayTypeId === null ||
    !(selectedWorkingDayTypeId in LOCKED_WEEKS_COUNT_BY_TYPE);
  const lockedWeeksCount = selectedWorkingDayTypeId !== null
    ? LOCKED_WEEKS_COUNT_BY_TYPE[selectedWorkingDayTypeId]
    : undefined;

  // Jornada fija/Parcial siempre usan 1 semana y Bisemanal siempre usa 2 — si el valor quedó
  // en otro número por una selección de tipo anterior, se resetea al bloquear el campo.
  const weeksCountValue = watch('weeksCount');
  useEffect(() => {
    const isMismatched = lockedWeeksCount !== undefined && weeksCountValue !== lockedWeeksCount;
    if (!weeksCountEditable && isMismatched) {
      setValue('weeksCount', lockedWeeksCount, { shouldDirty: true, shouldValidate: true });
    }
  }, [weeksCountEditable, lockedWeeksCount, weeksCountValue, setValue]);

  // Al cambiar el Tipo de jornada se limpia el resto del formulario — de lo contrario datos
  // de una selección anterior (ej. Número resolución DT de "Excepcional") reaparecen al volver
  // a seleccionar ese tipo (ASIS-5050, feedback QA). Se omite en el primer render (montaje).
  // `shouldValidate` se omite a propósito: los campos recién limpiados no deben marcarse como
  // inválidos hasta que el usuario interactúe con ellos (mode: 'onBlur').
  // `clearErrors` sí es necesario: si un campo ya tenía un error visible por un blur previo,
  // el mensaje en rojo queda pegado al valor anterior y no desaparece solo con `setValue`.
  const previousWorkingDayTypeIdRef = useRef(selectedWorkingDayTypeId);
  useEffect(() => {
    if (previousWorkingDayTypeIdRef.current === selectedWorkingDayTypeId) return;
    previousWorkingDayTypeIdRef.current = selectedWorkingDayTypeId;

    setValue('workingDayId', null, { shouldDirty: true });
    setValue('shiftCode', '', { shouldDirty: true });
    setValue('shiftName', '', { shouldDirty: true });
    const resetWeeksCount = selectedWorkingDayTypeId !== null
      ? (LOCKED_WEEKS_COUNT_BY_TYPE[selectedWorkingDayTypeId] ?? 1)
      : 1;
    setValue('weeksCount', resetWeeksCount, { shouldDirty: true });
    setValue('staticStartDate', '', { shouldDirty: true });
    setValue('isExceptionalWorkingDay', false, { shouldDirty: true });
    setValue('dtAuthorizationNumber', '', { shouldDirty: true });
    setValue('dtAuthorizationDate', '', { shouldDirty: true });
    clearErrors(RESET_ON_TYPE_CHANGE_FIELDS);
  }, [selectedWorkingDayTypeId, setValue, clearErrors]);

  // Los campos de resolución DT también se limpian al desmarcar "Jornada excepcional" sin
  // cambiar de tipo (se mantiene en Distribuida por ciclos) — si no, la data anterior
  // reaparece al volver a marcar el checkbox.
  // Mismo motivo que arriba: sin `shouldValidate`, con `clearErrors`.
  const isExceptionalWorkingDayValue = watch('isExceptionalWorkingDay');
  const showDtAuthorizationFields =
    isExceptionalType || (isDistributedCyclesType && isExceptionalWorkingDayValue);
  const dtAuthorizationNumberValue = watch('dtAuthorizationNumber');
  const dtAuthorizationDateValue = watch('dtAuthorizationDate');
  useEffect(() => {
    if (showDtAuthorizationFields) return;
    if (dtAuthorizationNumberValue) {
      setValue('dtAuthorizationNumber', '', { shouldDirty: true });
    }
    if (dtAuthorizationDateValue) {
      setValue('dtAuthorizationDate', '', { shouldDirty: true });
    }
    clearErrors(['dtAuthorizationNumber', 'dtAuthorizationDate']);
  }, [
    showDtAuthorizationFields,
    dtAuthorizationNumberValue,
    dtAuthorizationDateValue,
    setValue,
    clearErrors,
  ]);

  const workingDayOptions = useMemo<SelectorOption[]>(() => {
    const enabledWorkingDays = workingDays.filter((workingDay) => workingDay.status === 'enabled');
    const filtered = selectedWorkingDayTypeId !== null
      ? enabledWorkingDays.filter(
        (workingDay) => workingDay.workingDayTypeId === selectedWorkingDayTypeId,
      )
      : enabledWorkingDays;
    return filtered.map((workingDay) => ({ value: String(workingDay.id), label: workingDay.name }));
  }, [workingDays, selectedWorkingDayTypeId]);

  const selectedWorkingDay = useMemo(
    () => workingDays.find((workingDay) => workingDay.id === selectedWorkingDayId),
    [workingDays, selectedWorkingDayId],
  );

  const currentWeeklyHours = useMemo(() => {
    const today = new Date().getTime();
    const applicable = progressivities
      .filter((progressivity) => new Date(progressivity.rawStartDate).getTime() <= today)
      .sort((left, right) =>
        new Date(right.rawStartDate).getTime() - new Date(left.rawStartDate).getTime(),
      );
    return applicable[0]?.weeklyHours ?? null;
  }, [progressivities]);

  const isFixedOrByShiftType =
    selectedWorkingDayTypeId === WORKING_DAY_TYPE_ID.FIXED ||
    selectedWorkingDayTypeId === WORKING_DAY_TYPE_ID.BY_SHIFT;

  const ordinaryHours = useMemo(() => {
    if (isFixedOrByShiftType) {
      return currentWeeklyHours;
    }
    if (selectedWorkingDayTypeId === WORKING_DAY_TYPE_ID.PARTIAL) {
      return PARTIAL_WORKING_DAY_ORDINARY_HOURS;
    }
    if (selectedWorkingDayTypeId === WORKING_DAY_TYPE_ID.BIWEEKLY) {
      // Techo dinámico (confirmado por negocio 2026-07-17, ver NE_ShiftLegalRulesEvaluator.
      // EvaluateBiweeklyLimit en el backend): días totales del ciclo (trabajo + descanso)
      // ÷ 7 × progresividad vigente. No se resta ningún día de descanso adicional.
      const workingDaysPattern = selectedWorkingDay?.workingDays;
      const offDaysPattern = selectedWorkingDay?.offDays;
      if (!workingDaysPattern || !offDaysPattern || currentWeeklyHours === null) {
        return null;
      }
      const totalCycleDays = workingDaysPattern + offDaysPattern;
      return (totalCycleDays / DAYS_PER_WEEK) * currentWeeklyHours;
    }
    if (isDistributedCyclesType || isExceptionalType) {
      return selectedWorkingDay?.ordinaryWeeklyHours ?? null;
    }
    return null;
  }, [
    isFixedOrByShiftType,
    selectedWorkingDayTypeId,
    currentWeeklyHours,
    isDistributedCyclesType,
    isExceptionalType,
    selectedWorkingDay,
  ]);

  // ── Navegación ─────────────────────────────────────────────────────────
  const navigateToList = useCallback(() => {
    void navigate(ROUTE_DEFINITIONS.TURNOS_ROTATIVOS_FIJOS.path);
  }, [navigate]);

  const { handleCancelWithWarning: handleCancel, confirmNavigation } = useUnsavedChangesWarning({
    onConfirmLeave: navigateToList,
    isDirty,
  });

  return {
    formSubset,
    isLoadingCatalogs,
    isDirty,
    workingDayTypeOptions,
    workingDayOptions,
    selectedWorkingDayTypeId,
    selectedWorkingDay,
    isDistributedCyclesType,
    isExceptionalType,
    isStaticType,
    ordinaryHours,
    weeksCountEditable,
    isGridVisible,
    handleCancel,
    confirmNavigation,
  };
}
